import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { LOGO_URL, HERO_IMAGE } from "../lib/constants";
import SocialAuthButtons from "../components/SocialAuthButtons";

const ROLES = [
  { id: "cliente", label: "Cliente", desc: "Publico proyectos" },
  { id: "contratista", label: "Maestro-Contratista", desc: "Ejecuto obras" },
  { id: "ferreteria", label: "Ferretería", desc: "Vendo materiales" },
];

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "cliente" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    const r = await register(form);
    setLoading(false);
    if (r.ok) navigate("/");
    else setError(r.error);
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50" data-testid="register-page">
      <div
        className="hidden lg:flex relative overflow-hidden text-white p-12 flex-col justify-between"
        style={{ background: "linear-gradient(135deg, #0B1E36 0%, #1E3A8A 100%)" }}
      >
        <img src={HERO_IMAGE} alt="" className="absolute inset-0 w-full h-full object-cover opacity-15" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B1E36]/95 via-[#0B1E36]/80 to-[#1E3A8A]/70" />
        <div className="relative">
          {/* Logo removido — se muestra centrado en el formulario a la derecha */}
        </div>
        <div className="relative">
          <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-brand-gold">
            Crea tu ConstruLink ID
          </p>
          <h1 className="mt-3 text-4xl xl:text-5xl font-extrabold leading-tight">
            Únete al <br />
            <span className="text-brand-gold">Ecosistema</span> más grande de la <br />
            construcción.
          </h1>
          <p className="mt-4 text-slate-300 text-base max-w-md">
            Una sola cuenta para acceder a las cinco soluciones. Empieza hoy tu transformación digital.
          </p>
        </div>
        <div className="relative text-[11px] text-slate-400">
          © {new Date().getFullYear()} ConstruLink · Plataforma Integral para la Industria de la Construcción
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-10">
        <div className="w-full max-w-md">
          {/* Logo protagonista sobre tarjeta corporativa con contraste y sombra */}
          <div className="mb-8" data-testid="register-logo-block">
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-b from-white to-slate-50 ring-1 ring-slate-200/70 shadow-[0_22px_55px_-22px_rgba(11,30,54,0.38)] px-8 py-8 flex items-center justify-center">
              <div aria-hidden className="absolute -top-12 -right-10 w-44 h-44 rounded-full bg-brand-blue/[0.06] blur-2xl" />
              <div aria-hidden className="absolute -bottom-14 -left-10 w-44 h-44 rounded-full bg-brand-red/[0.06] blur-2xl" />
              <div aria-hidden className="absolute inset-x-8 bottom-4 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
              <img
                src={LOGO_URL}
                alt="ConstruLink"
                className="relative w-full max-w-md h-auto object-contain"
              />
            </div>
          </div>

          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand-red text-center">Registro</p>
          <h2 className="mt-2 text-3xl font-extrabold text-slate-900 tracking-tight text-center">
            Crea tu ConstruLink ID
          </h2>
          <p className="mt-1 text-slate-500 text-sm text-center">
            Toma menos de un minuto y no requiere tarjeta.
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="register-form">
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Nombre completo</label>
              <input
                type="text"
                required
                data-testid="register-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ej. Ana Rodríguez"
                className="mt-1.5 w-full h-12 px-4 rounded-xl border border-slate-200 bg-white focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 outline-none"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Correo</label>
              <input
                type="email"
                required
                data-testid="register-email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="tu@empresa.com"
                className="mt-1.5 w-full h-12 px-4 rounded-xl border border-slate-200 bg-white focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 outline-none"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Contraseña</label>
              <input
                type="password"
                required
                minLength={6}
                data-testid="register-password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Mínimo 6 caracteres"
                className="mt-1.5 w-full h-12 px-4 rounded-xl border border-slate-200 bg-white focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 outline-none"
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Soy…</label>
              <div className="mt-1.5 grid grid-cols-3 gap-2">
                {ROLES.map((r) => (
                  <button
                    type="button"
                    key={r.id}
                    data-testid={`register-role-${r.id}`}
                    onClick={() => setForm({ ...form, role: r.id })}
                    className={[
                      "text-left p-2.5 rounded-xl border transition-all",
                      form.role === r.id
                        ? "border-brand-red bg-red-50/60"
                        : "border-slate-200 bg-white hover:border-slate-300",
                    ].join(" ")}
                  >
                    <p className="text-xs font-bold text-slate-900">{r.label}</p>
                    <p className="text-[10px] text-slate-500">{r.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {error && (
              <p className="text-sm text-brand-red bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="register-error">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="register-submit"
              className="w-full h-12 inline-flex items-center justify-center gap-2 bg-brand-red hover:bg-brand-red-hover disabled:opacity-60 text-white font-semibold rounded-xl transition-colors shadow-sm"
            >
              {loading ? "Creando…" : (<>Crear cuenta <ArrowRight size={16} /></>)}
            </button>
          </form>

          <SocialAuthButtons />

          <p className="mt-8 text-sm text-center text-slate-600">
            ¿Ya tienes cuenta?{" "}
            <Link to="/login" className="font-semibold text-brand-red hover:underline" data-testid="link-login">
              Inicia sesión
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
